import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Drives the media library's bulk Archive/Restore actions. The bulk-select
// controller (on the data_table wrapper) owns the action bar UI — showing it,
// counting selections, and toggling the buttons. This controller only reacts to
// the bar's buttons: Archive opens a confirmation modal, Restore fires straight
// away (restoring is always safe). Both PATCH a collection route with the
// selected ids, then reload the list frame via a window event on success.
const RELOAD_EVENT = "media:list-changed"

export default class extends Controller {
  static targets = ["modal", "count", "successToast", "errorToast"]
  static values = { archiveUrl: String, restoreUrl: String }

  open(event) {
    if (event) event.preventDefault()

    const ids = this.selectedIds()
    if (ids.length === 0) return

    if (this.hasCountTarget) {
      const noun = ids.length === 1 ? "item" : "items"
      this.countTarget.textContent = `${ids.length} ${noun}`
    }

    Modal.getOrCreateInstance(this.modalTarget).show()
  }

  async submit(event) {
    if (event) event.preventDefault()

    const ids = this.selectedIds()
    if (ids.length === 0) return

    try {
      const response = await this.patch(this.archiveUrlValue, ids)
      if (!response.ok) return this.showSnackbar("error", "Couldn't archive the selected media")

      Modal.getOrCreateInstance(this.modalTarget).hide()
      this.notifyListChanged()
      this.showSnackbar("success", this.summary(ids.length, "archived"))
    } catch {
      this.showSnackbar("error", "Couldn't archive the selected media")
    }
  }

  async restore(event) {
    if (event) event.preventDefault()

    const ids = this.selectedIds()
    if (ids.length === 0) return

    try {
      const response = await this.patch(this.restoreUrlValue, ids)
      if (!response.ok) return this.showSnackbar("error", "Couldn't restore the selected media")

      this.notifyListChanged()
      this.showSnackbar("success", this.summary(ids.length, "restored"))
    } catch {
      this.showSnackbar("error", "Couldn't restore the selected media")
    }
  }

  selectedIds() {
    return [...this.element.querySelectorAll('[data-bulk-select-target="checkbox"]:checked')]
      .filter(cb => !cb.closest("tr[hidden]"))
      .map(cb => cb.value)
  }

  summary(count, verb) {
    const noun = count === 1 ? "media item" : "media items"
    return `${count} ${noun} ${verb}`
  }

  patch(url, ids) {
    return fetch(url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": this.csrfToken(),
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({ ids })
    })
  }

  notifyListChanged() {
    window.dispatchEvent(new CustomEvent(RELOAD_EVENT))
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  showSnackbar(type, title) {
    const template = type === "error"
      ? (this.hasErrorToastTarget ? this.errorToastTarget : null)
      : (this.hasSuccessToastTarget ? this.successToastTarget : null)
    if (!template) return

    const node = template.content.firstElementChild.cloneNode(true)
    const titleEl = node.querySelector(".snackbar-title span")
    if (titleEl) titleEl.textContent = title

    this.flashContainer().appendChild(node)
  }

  flashContainer() {
    let container = document.querySelector(".app-flash-alert-container")
    if (!container) {
      container = document.createElement("div")
      container.className = "app-flash-alert-container"
      document.body.appendChild(container)
    }
    return container
  }
}
